/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-types */

import { isPlainObject } from "lodash";
import { QueryParams } from "../../../core/http/query-params.type";
import { AnyObject, SelectProperty } from "../../types/app.type";

export function removePropsWithKeys<T>(
  filter: QueryParams<T>["where"],
  keys: string[] = ["", null, undefined],
): QueryParams<T>["where"] {
  if (!filter) {
    return {};
  }
  if (Array.isArray(filter)) {
    return filter.filter((v) => Object.keys(v).some((prop) => !keys.includes(prop)));
  }
  if (Object.keys(filter).length) {
    return Object.entries(filter)
      .filter(([k, _]) => keys.indexOf(k) === -1)
      .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});
  }
  return [];
}

export function removePropsWithValues(obj: {}, values: any[] = ["", null, undefined]): AnyObject {
  return Object.entries(obj)
    .filter(([_, v]) => values.indexOf(v) === -1)
    .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});
}

export function setValueToDeeperProperty<T = any>(
  value: any,
  object: SelectProperty<T> | SelectProperty<T>[],
): AnyObject {
  const keys = Object.keys(object);

  const key = keys[0];
  if (typeof object[key] === "object") {
    const _object: AnyObject = { ...object };
    _object[key] = setValueToDeeperProperty(value, _object[key]);
    return _object;
  }
  if (object[key] === true) {
    return { [key]: value };
  }
  throw new Error("Defined object is not defined properly. Deeper property must be 'true'");
}

/** Convert a simple object like `{ a: { b: true } }` in `a.b` */
export function simpleObjectToDotNotationString(obj: AnyObject, acc: string = ""): string {
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      acc = acc ? acc + "." + key : key;
      if (isPlainObject(obj[key])) {
        acc = simpleObjectToDotNotationString(obj[key], acc);
      }
    }
  }
  return acc;
}

/** Get the value of a object using dot notation (like `"prop.childProp"`) */
export function getObjectValueByDotNotation(obj: AnyObject, dotNotation: string): unknown {
  return dotNotation?.split(".").reduce((a, b) => a?.[b], obj);
}

/**
 * Merge object recursively
 *
 * Example:
 *
 * `const a = { x: 'x', y: 'a', z: { x: 'x', y: 'a' } };`
 *
 * `const b = { y: 'y', z: { y: 'y', z: undefined } };`
 *
 * `mergeObjects([a, b]);`
 *
 * returns `{ x: 'x', y: 'y', z: { x: 'x', y: 'y', z: undefined } }`
 *
 * @param objects objects to merge (inner objects are merged without being overwritten)
 * @param options `options.omitUndefinedProps`: if it's true then undefined props will be omitted when merging
 * @returns a plain object
 */
export function mergeObjects<T = AnyObject>(
  objects: T[],
  options?: { omitProps?: (keyof T)[]; omitUndefinedProps?: boolean },
): T {
  const omitProps = options?.omitProps;
  const omitUndefinedProps = !!options?.omitUndefinedProps;

  const mergedObj = {} as T;

  objects
    .filter((obj) => obj)
    .forEach((obj) => {
      const currKeys = Object.keys(obj);

      currKeys.forEach((key) => {
        if (omitProps?.includes(key as keyof T)) {
          return;
        }
        const currentValueInMergedObj = mergedObj[key];
        const currentValue = obj[key];
        const shouldMerge =
          typeof currentValue === "object" &&
          currentValue !== null &&
          Object.getPrototypeOf(currentValue) === Object.prototype;

        if (shouldMerge) {
          const _objects = [];
          if (currentValueInMergedObj !== undefined && typeof currentValueInMergedObj === "object") {
            _objects.push(currentValueInMergedObj);
          }
          if (currentValue !== undefined) {
            _objects.push(currentValue);
          }
          mergedObj[key] = mergeObjects([..._objects], { omitUndefinedProps });
        } else if ((omitUndefinedProps && currentValue !== undefined) || !omitUndefinedProps) {
          mergedObj[key] = currentValue;
        }
      });
    });

  return mergedObj;
}

/** Set the values of empty strings to null. Useful for update methods */
export function setEmptyStringsToNull(object: AnyObject): void {
  Object.keys(object).forEach((key) => {
    if (object[key] === "") {
      object[key] = null;
    }
  });
}

/**
 * Converts a dot notation key into an object.
 *
 * @param {string} key - The dot notation key.
 * @param {object} [obj={}] - The object to populate with the key-value pair.
 * @returns {object} The resulting object.
 */
export function dotNotationToObject(key: string, obj: AnyObject = {}): AnyObject {
  const hasToSplit = key.toString().indexOf(".") > -1;

  if (hasToSplit) {
    const keys = key.split(".");
    const keysString = keys.slice(0, -1).join(".");
    const value = Object.values(obj)[0];
    if (Object.keys(obj).length) {
      return dotNotationToObject(keysString, {
        [keysString]: { [keys.slice(-1)[0]]: value },
      });
    }
    return dotNotationToObject(keysString, {
      [keysString]: { [keys.slice(-1)[0]]: true },
    });
  } else if (Object.keys(obj).length) {
    return obj;
  }

  return { [key]: true };
}
